'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LeviButton from '@/app/components/LeviButton';
import Scribble from '@/app/components/suppliers/Scribble';

const ProductCloud = dynamic(() => import('@/app/components/suppliers/ProductCloud'), { ssr: false });

export default function OfferSection({ id }: { id: string }) {
  const root = useRef<HTMLElement>(null);
  const pointer = useRef({ x: 0, y: 0 });
  const drift = useRef(0);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = root.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { rootMargin: '15% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.create({
      trigger: root.current, start: 'top bottom', end: 'bottom top', scrub: true,
      onUpdate: (self) => { drift.current = (self.progress - 0.5) * 2.4; },
    });
    gsap.from('.sup-offer-card', {
      y: 60, opacity: 0, rotate: (i) => (i ? 2 : -2), duration: 0.9, stagger: 0.18, ease: 'power3.out',
      scrollTrigger: { trigger: '.sup-offer-cards', start: 'top 75%', once: true },
    });
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      pointer.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      pointer.current.y = (e.clientY / window.innerHeight) * 2 - 1;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, { scope: root });

  return (
    <section ref={ root } id={ id } className='relative mt-24 md:mt-40 py-16 md:py-28 overflow-hidden'>
      <ProductCloud pointer={ pointer } drift={ drift } active={ visible } className='absolute inset-0 pointer-events-none' />
      <div className='container relative z-10'>
        <div className='text-center'>
          <h2 className='sup-h2 relative inline-block'>Co pro vás uděláme
            <Scribble kind='zigzag' trigger='scrub' start='top 85%' end='top 45%' color='var(--sup-heat)' width={ 6 } style={ { left: '2%', right: '2%', bottom: '-0.22em', height: '0.22em' } } />
          </h2>
          <p className='sup-body mx-auto mt-6 text-center'>Ať prodáváte konvektomaty, kávu, maso nebo brčka, dostaneme vás k restauratérům.</p>
        </div>

        <div className='sup-offer-cards mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-[68rem] mx-auto'>
          <div className='sup-offer-card sup-card p-8 md:p-12'>
            <div className='sup-h3'>Prezentace vašeho byznysu</div>
            <p className='sup-body mt-5'>
              Připravíme prezentaci vašeho byznysu, která majitele podniků a provozní zaujme. Ne datasheet, ale odpověď na otázku „co mi to přinese v provozu“.
            </p>
            <div className='mt-6 flex flex-wrap gap-2'>
              { ['argumenty pro provozní', 'vizuály & video', 'landing page'].map((t) => <span key={ t } className='sup-chip text-[0.85rem]! py-2! px-3!'>{ t }</span>) }
            </div>
          </div>
          <div className='sup-offer-card sup-card sup-card--purple p-8 md:p-12 md:translate-y-10'>
            <div className='sup-h3'>Reklama, která nosí poptávky</div>
            <p className='sup-body mt-5 text-white/90'>
              Připravíme vám reklamu, která přinese relevantní poptávky. Cílíme na majitele a provozní, ne na hosty. Měříme cenu za poptávku, ne lajky.
            </p>
            <div className='mt-6 flex flex-wrap gap-2'>
              { ['Meta & Instagram', 'cílení na podniky', 'poptávky do e-mailu'].map((t) => <span key={ t } className='sup-chip text-[0.85rem]! py-2! px-3! bg-white! border-white! text-black!'>{ t }</span>) }
            </div>
          </div>
        </div>

        <div className='mt-16 md:mt-24 flex flex-col items-center gap-4 relative'>
          <div className='relative inline-block'>
            <Scribble kind='arrow' color='var(--sup-heat)' width={ 6 } className='hidden md:block' style={ { left: '-190px', top: '-120px', width: '150px', height: '150px' } } />
            <LeviButton href='#kontakt' color='black' className='text-lg md:text-xl'>Chci návrh zdarma</LeviButton>
          </div>
          <span className='text-[0.9rem] font-bold opacity-60'>Bez závazků. Do 2 pracovních dnů.</span>
        </div>
      </div>
    </section>
  );
}
